import { getAuthToken, handleUnauthorized } from '@/lib/auth';

export type SttResponse = {
  text: string;
  language?: string;
  segments?: unknown[];
};

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;

export async function transcribeAudio(params: {
  blob: Blob;
  filename?: string;
  language?: string;
  includeSegments?: boolean;
  signal?: AbortSignal;
}): Promise<SttResponse> {
  const { blob, filename = 'audio.webm', language, includeSegments, signal } = params;
  const query = new URLSearchParams();
  if (language) {
    query.set('language', language);
  }
  if (includeSegments) {
    query.set('include_segments', 'true');
  }

  const formData = new FormData();
  formData.append('audio', blob, filename);
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${API_BASE_URL}/stt${query.toString() ? `?${query.toString()}` : ''}`,
    {
      method: 'POST',
      body: formData,
      headers,
      signal,
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorized();
    }

    const detail = await response.text().catch(() => '');
    if (response.status === 503) {
      throw new Error(
        'Voice transcription is temporarily unavailable. Please try again in a few moments.'
      );
    }

    throw new Error(
      `STT request failed (${response.status}): ${detail || response.statusText}`
    );
  }

  return (await response.json()) as SttResponse;
}
